<template>
  <div class="dashboard">
    <div class="dashboard-inner">
      <header class="dashboard-header">
        <p class="dashboard-eyebrow">Your Developmental Edit</p>
        <h1 class="dashboard-title">{{ projectStore.title || 'Untitled Manuscript' }}</h1>
        <p v-if="projectStore.author" class="dashboard-author">{{ projectStore.author }}</p>
      </header>

      <div class="framework-sections">
        <section v-for="section in frameworkSections" :key="section.frameworkId" class="framework-section">
          <template v-if="section.type === 'standard'">
            <h2 class="section-label">{{ section.frameworkName }}</h2>
            <div class="card-grid">
              <router-link v-for="card in section.cards" :key="card.outputName" :to="card.url" class="deliverable-card">
                <div class="card-icon">
                  <QIcon :path="card.icon" :size="28" color="var(--color-accent)" />
                </div>
                <div class="card-badge" :class="card.status === 'complete' ? 'badge-ready' : 'badge-processing'">
                  {{ card.status === 'complete' ? 'Ready' : 'Processing' }}
                </div>
                <h3 class="card-title">{{ card.displayName }}</h3>
                <p class="card-description">{{ card.description }}</p>
              </router-link>
            </div>
          </template>

          <template v-else-if="section.type === 'query'">
            <h2 class="section-label">{{ section.frameworkName }}</h2>

            <div v-if="section.guideCard" class="card-grid card-grid-1">
              <router-link :to="section.guideCard.url" class="deliverable-card">
                <div class="card-icon">
                  <QIcon :path="section.guideCard.icon" :size="28" color="var(--color-accent)" />
                </div>
                <div
                  class="card-badge"
                  :class="section.guideCard.status === 'complete' ? 'badge-ready' : 'badge-processing'"
                >
                  {{ section.guideCard.status === 'complete' ? 'Ready' : 'Processing' }}
                </div>
                <h3 class="card-title">{{ section.guideCard.displayName }}</h3>
                <p class="card-description">{{ section.guideCard.description }}</p>
              </router-link>
            </div>

            <div v-if="section.queryLetterCards.length" class="query-subgroup">
              <p class="subgroup-label">Query Letters</p>
              <div class="card-grid card-grid-3">
                <router-link
                  v-for="card in section.queryLetterCards"
                  :key="card.outputName"
                  :to="card.url"
                  class="deliverable-card"
                >
                  <div class="card-icon">
                    <QIcon :path="card.icon" :size="28" color="var(--color-accent)" />
                  </div>
                  <div class="card-badge" :class="card.status === 'complete' ? 'badge-ready' : 'badge-processing'">
                    {{ card.status === 'complete' ? 'Ready' : 'Processing' }}
                  </div>
                  <h3 class="card-title">{{ card.displayName }}</h3>
                  <p class="card-description">{{ card.description }}</p>
                </router-link>
              </div>
            </div>

            <div v-if="section.synopsisCards.length" class="query-subgroup">
              <p class="subgroup-label">Synopses</p>
              <div class="card-grid card-grid-3">
                <router-link
                  v-for="card in section.synopsisCards"
                  :key="card.outputName"
                  :to="card.url"
                  class="deliverable-card"
                >
                  <div class="card-icon">
                    <QIcon :path="card.icon" :size="28" color="var(--color-accent)" />
                  </div>
                  <div class="card-badge" :class="card.status === 'complete' ? 'badge-ready' : 'badge-processing'">
                    {{ card.status === 'complete' ? 'Ready' : 'Processing' }}
                  </div>
                  <h3 class="card-title">{{ card.displayName }}</h3>
                  <p class="card-description">{{ card.description }}</p>
                </router-link>
              </div>
            </div>
          </template>
        </section>
      </div>

      <footer class="dashboard-footer">
        <QIcon :path="mdiCheckCircle" :size="16" color="#1a7f37" />
        <span>Analysis complete &middot; {{ totalDeliverables }} deliverables ready</span>
      </footer>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  mdiBookOpenVariant,
  mdiCheckCircle,
  mdiCompass,
  mdiEmailOutline,
  mdiFileDocumentOutline,
  mdiFireAlert,
  mdiFormatListChecks,
  mdiScriptTextOutline,
} from '@mdi/js';
import { computed } from 'vue';
import { buildOutputUrl, stripFrameworkPrefix } from '../lib/urlUtils';
import { useProjectStore } from '../stores/project';
import QIcon from './QIcon.vue';

const projectStore = useProjectStore();

interface CardDef {
  outputName: string;
  displayName: string;
  icon: string;
  description: string;
  status: string;
  url: string;
}

interface StandardSection {
  type: 'standard';
  frameworkId: string;
  frameworkName: string;
  cards: CardDef[];
}

interface QuerySection {
  type: 'query';
  frameworkId: string;
  frameworkName: string;
  guideCard: CardDef | null;
  queryLetterCards: CardDef[];
  synopsisCards: CardDef[];
}

type FrameworkSection = StandardSection | QuerySection;

const OUTPUT_META: Record<string, { displayName: string; icon: string; description: string }> = {
  welcome_letter: { displayName: 'Welcome Letter', icon: mdiEmailOutline, description: "Your editor's opening note" },
  editorial_letter: {
    displayName: 'Editorial Letter',
    icon: mdiScriptTextOutline,
    description: 'Core feedback and priorities',
  },
  revision_plan: {
    displayName: 'Revision Plan',
    icon: mdiFormatListChecks,
    description: 'Your action items, step by step',
  },
  chapter_notes: { displayName: 'Chapter Notes', icon: mdiBookOpenVariant, description: 'Scene-by-scene analysis' },
  roast_editorial_letter: {
    displayName: 'Editorial Letter',
    icon: mdiFireAlert,
    description: 'Where your story gets roasted',
  },
  roast_chapter_notes: { displayName: 'Chapter Notes', icon: mdiBookOpenVariant, description: 'Scene-by-scene roast' },
  query_letter_welcome_guide: {
    displayName: 'Personalization Guide',
    icon: mdiCompass,
    description: 'Finding the right agents for your book',
  },
  query_letter_variation_1: {
    displayName: 'Variation 1',
    icon: mdiEmailOutline,
    description: 'First query letter variation',
  },
  query_letter_variation_2: {
    displayName: 'Variation 2',
    icon: mdiEmailOutline,
    description: 'Second query letter variation',
  },
  query_letter_variation_3: {
    displayName: 'Variation 3',
    icon: mdiEmailOutline,
    description: 'Third query letter variation',
  },
  query_letter_synopsis_short: {
    displayName: 'Half-Page Synopsis',
    icon: mdiFileDocumentOutline,
    description: 'Concise story summary',
  },
  query_letter_synopsis_medium: {
    displayName: 'One-Page Synopsis',
    icon: mdiFileDocumentOutline,
    description: 'Standard submission synopsis',
  },
  query_letter_synopsis_long: {
    displayName: 'Two-Page Synopsis',
    icon: mdiFileDocumentOutline,
    description: 'Detailed story overview',
  },
};

function makeCard(frameworkId: string, output: { name: string; status: string }): CardDef {
  const strippedName = stripFrameworkPrefix(output.name, frameworkId.replace(/-/g, '_'));
  const meta = OUTPUT_META[output.name] ??
    OUTPUT_META[strippedName] ?? {
      displayName: strippedName.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()),
      icon: mdiFileDocumentOutline,
      description: '',
    };
  return {
    outputName: output.name,
    displayName: meta.displayName,
    icon: meta.icon,
    description: meta.description,
    status: output.status,
    url: buildOutputUrl(projectStore.projectId, frameworkId, output.name),
  };
}

const frameworkSections = computed<FrameworkSection[]>(() => {
  return projectStore.frameworks
    .filter(f => f.frameworkId !== 'benchmark')
    .map(framework => {
      if (framework.frameworkId === 'query-letter') {
        const guide = framework.outputs.find(o => o.name.includes('welcome_guide'));
        const variations = framework.outputs.filter(o => /variation_\d/.test(o.name));
        const synopses = framework.outputs.filter(o => o.name.includes('synopsis'));
        return {
          type: 'query' as const,
          frameworkId: framework.frameworkId,
          frameworkName: framework.name,
          guideCard: guide ? makeCard(framework.frameworkId, guide) : null,
          queryLetterCards: variations.map(o => makeCard(framework.frameworkId, o)),
          synopsisCards: synopses.map(o => makeCard(framework.frameworkId, o)),
        };
      }
      return {
        type: 'standard' as const,
        frameworkId: framework.frameworkId,
        frameworkName: framework.name,
        cards: framework.outputs
          .filter(o => !o.name.endsWith('_highlights'))
          .map(o => makeCard(framework.frameworkId, o)),
      };
    });
});

const totalDeliverables = computed(() => {
  return projectStore.frameworks
    .filter(f => f.frameworkId !== 'benchmark')
    .reduce(
      (sum, f) => sum + f.outputs.filter(o => o.status === 'complete' && !o.name.endsWith('_highlights')).length,
      0
    );
});
</script>

<style scoped>
.dashboard {
  flex: 1;
  background: var(--color-bg-secondary);
  overflow-y: auto;
}

.dashboard-inner {
  max-width: 860px;
  margin: 0 auto;
  padding: 3rem 2rem 4rem;
}

.dashboard-header {
  text-align: center;
  margin-bottom: 3rem;
}

.dashboard-eyebrow {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--color-text-muted);
  margin-bottom: 0.5rem;
}

.dashboard-title {
  font-family: 'PT Serif', serif;
  font-size: 2rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--color-text-primary);
  margin: 0 0 0.5rem;
}

.dashboard-author {
  font-family: 'PT Serif', serif;
  font-style: italic;
  font-size: 1rem;
  color: var(--color-text-secondary);
  margin: 0;
}

.framework-sections {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}

.framework-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.section-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-text-muted);
  margin: 0;
}

.subgroup-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-text-muted);
  margin: 0.5rem 0 0;
}

.query-subgroup {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
}

.card-grid-1 {
  grid-template-columns: repeat(1, minmax(0, 320px));
}

.card-grid-3 {
  grid-template-columns: repeat(3, 1fr);
}

@media (max-width: 640px) {
  .card-grid,
  .card-grid-3 {
    grid-template-columns: 1fr;
  }
}

.deliverable-card {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: white;
  border: 1px solid var(--color-border-primary);
  border-radius: 8px;
  padding: 1.5rem;
  text-decoration: none;
  color: inherit;
  transition:
    transform 0.2s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.deliverable-card:hover {
  transform: translateY(-3px);
  box-shadow:
    0 10px 25px -5px rgba(0, 0, 0, 0.1),
    0 8px 10px -6px rgba(0, 0, 0, 0.05);
  border-color: var(--color-accent);
}

.card-icon {
  margin-bottom: 0.875rem;
  line-height: 1;
  pointer-events: none;
}

.card-badge {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 2px 8px;
  border-radius: 999px;
  margin-bottom: 0.625rem;
}

.badge-ready {
  background: #dafbe1;
  color: #1a7f37;
}

.badge-processing {
  background: #bee3f8;
  color: #2c5282;
}

.card-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 0.375rem;
}

.card-description {
  font-family: 'PT Serif', serif;
  font-style: italic;
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  line-height: 1.5;
  margin: 0;
}

.dashboard-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2.5rem;
  font-size: 0.875rem;
  color: var(--color-text-muted);
}
</style>
